import './PrivacyPolicy.css';

/**
 * Privacy policy page for SUSU Closet browser extension.
 * Required for Chrome Web Store listing.
 */
const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <a href="/" className="privacy-back" aria-label="Back to closet">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </a>
          <h1>Privacy Policy</h1>
          <p className="privacy-updated">Last updated: April 4, 2026</p>
        </header>

        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            SUSU Closet ("we", "our", or "us") provides a virtual closet and try-on platform
            at <strong>susucloset.com</strong> along with the <strong>SUSU Closet browser extension</strong> for
            Google Chrome. We are committed to protecting your privacy. This policy explains what
            data we collect, how we use it, and your rights.
          </p>
        </section>

        <section className="privacy-section">
          <h2>What the Browser Extension Does</h2>
          <p>
            The SUSU Closet browser extension lets you right-click images on any website and save
            them to your personal SUSU Closet account — either as clothing items or person images
            for virtual try-on.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data We Collect</h2>
          <div className="privacy-table-wrap">
            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>When</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Image URL</td>
                  <td>Only when you explicitly right-click and save</td>
                  <td>To download and store the image in your closet</td>
                </tr>
                <tr>
                  <td>Page URL &amp; title</td>
                  <td>Only when you save an image</td>
                  <td>To auto-fill the item name field (optional)</td>
                </tr>
                <tr>
                  <td>Account credentials</td>
                  <td>When you log in via the extension</td>
                  <td>To authenticate your SUSU Closet account</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Data We Do NOT Collect</h2>
          <ul className="privacy-list">
            <li>Browsing history or activity</li>
            <li>Cookies or tracking data from other websites</li>
            <li>Personal information beyond your SUSU Closet account</li>
            <li>Analytics or usage telemetry</li>
            <li>Data from pages you do not interact with</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Why We Need "All Sites" Permission</h2>
          <p>
            The extension requests access to all websites (<code>&lt;all_urls&gt;</code>) because you
            may want to save images from <em>any</em> site — Pinterest, Instagram, online stores,
            fashion blogs, or any other page. The content script only activates when you right-click
            an image. <strong>No data is collected passively or in the background.</strong>
          </p>
        </section>

        <section className="privacy-section">
          <h2>How We Store Your Data</h2>
          <ul className="privacy-list">
            <li>Images are uploaded to <strong>Cloudinary</strong> (cloud media storage) and linked to your account</li>
            <li>Account data is stored on our backend server with industry-standard security</li>
            <li>Authentication tokens are stored in Chrome's secure local extension storage</li>
            <li>We do not sell, share, or transfer your data to third parties</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Your Rights</h2>
          <ul className="privacy-list">
            <li><strong>Delete your data:</strong> You can delete any clothing item, person image, or virtual try-on result from your closet at any time</li>
            <li><strong>Delete your account:</strong> Contact us to permanently delete your account and all associated data</li>
            <li><strong>Export your data:</strong> You can download any image stored in your closet</li>
            <li><strong>Uninstall:</strong> Removing the extension deletes all locally stored tokens and preferences</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Children</h2>
          <p>
            SUSU Closet is not directed at children under 13. We do not knowingly collect personal
            information from children under 13.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes</h2>
          <p>
            We may update this privacy policy from time to time. Changes will be posted on this page
            with an updated revision date.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            Questions about this policy? Reach us at{' '}
            <a href="mailto:contact@sheyou.co">contact@sheyou.co</a>
          </p>
        </section>

        <footer className="privacy-footer">
          <p>SUSU Closet by SHEYOU</p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
